/**
 * Screened-patient worklist API contracts and table-row transformations.
 */

import { formatEligibilityTierLabel } from "../utils/stringModifications";
import { getEligibilityStatusBadgeColors } from "../utils/eligibilityStatusBadges";

/** One recent clinical document attached to an eligibility row. */
export interface EligibilityClinicalUpdate {
  document_id: string;
  document_type: string;
  document_name: string | null;
  created_at: string | null;
}

/** Latest screening execution attached to a patient/trial pair. */
export interface EligibilityScreening {
  status: string;
  run_id: string | null;
  triggered_at: string;
  triggered_by: string;
  started_at: string | null;
  completed_at: string | null;
  verdict: string | null;
  eligibility_tier: string | null;
  eligibility_status: string | null;
  stopped_at: string | null;
  skipped_reason: string | null;
}

/** One subscribed trial linked to the patient from the eligibility row. */
export interface EligibilityMonitoringTrial {
  trial_id: string;
  label: string;
  nct_id: string | null;
  sponsor: string | null;
  sponsor_protocol_id: string | null;
}

/** One patient returned by `POST /api/v1/eligibility/screened-rows`. */
export interface EligibilityScreenedRow {
  mrn: string;
  patient_name: string | null;
  diagnosis: string | null;
  diagnosis_code: string | null;
  stage: string | null;
  principal_md: string | null;
  insurance: string | null;
  insurance_category: string | null;
  next_md_visit: string | null;
  patient_status: string;
  eligibility_status: string | null;
  eligibility_tier: string | null;
  is_blacklisted: boolean;
  is_bookmarked: boolean;
  /** Whether the authenticated user has opened this patient row. */
  is_read: boolean;
  patient_fhir_identifier: string | null;
  open_note_count: number;
  clinical_updates: EligibilityClinicalUpdate[];
  additional_studies: string[];
  monitoring_trials?: EligibilityMonitoringTrial[];
  updated_at: string | null;
  updated_by: string | null;
  screening: EligibilityScreening | null;
  notifications?: EligibilityNotificationSummary;
}

/** Per-user Notes notification badge state included on an Eligibility row. */
export interface EligibilityNotificationSummary {
  has_unread: boolean;
  unread_count: number;
  latest_notification_at?: string | null;
  latest_event_type?: string | null;
}

/** Snake-case request body accepted by the screened worklist endpoint. */
export interface EligibilityScreenedRowsRequest {
  trial_id: string;
  query?: string;
  tab?: string;
  patient_status?: string;
  eligibility_status?: string;
  eligibility_tier?: string;
  additional_study?: string;
  stage?: string;
  diagnosis_code?: string;
  next_md_visit_from?: string;
  next_md_visit_to?: string;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  page?: number;
  hits_per_page?: number;
}

/** Paginated screened worklist response; page numbers are zero-indexed. */
export interface EligibilityScreenedRowsResponse {
  hits: EligibilityScreenedRow[];
  total_count: number;
  page: number;
  total_pages: number;
  hits_per_page: number;
}

/** Row shape consumed by the config-driven Patient Eligibility table. */
export interface EligibilityTableRow {
  id: string;
  patientDetails: {
    mainText: string;
    secondaryText: string;
    /** Identifier written to the clipboard from the MRN copy control. */
    copyValue: string;
    shouldShowLeftBorder: boolean;
    leftBorderColor: string;
    leftBorderWidth: number;
    primaryTextColor?: string;
  };
  stage: {
    stage: string;
    diagnosis: string;
  };
  diagnosis: {
    diagnosis: string;
    diagnosisCode: string;
  };
  principalMd: string;
  nextVisit: string;
  additionalTrials: {
    tags: string[];
    linkedTrialIds: string[];
  };
  patientStatus: { value: string; sortValue: string };
  eligibilityTier: {
    value: string | null;
    text: string;
    backgroundColor: string;
    foregroundColor: string;
  };
  eligibilityStatus: {
    text: string;
    backgroundColor: string;
    foregroundColor: string;
  };
  actionItems: {
    mrn: string;
    isBookmarked: boolean;
    notificationUnreadCount?: number;
    hasUnreadNotification?: boolean;
    onOpenNotifications?: () => void;
    onToggleBookmark?: () => void;
  };
}

const EMPTY_VALUE = "—";

/**
 * Format an ISO date/time for the worklist while preserving unknown values.
 *
 * @param value - ISO date/time from the eligibility API
 * @returns Locale-formatted date or an em dash
 */
export function formatEligibilityDate(value: string | null): string {
  if (!value) return EMPTY_VALUE;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      });
}

/** Optional catalogs used to display coded eligibility fields. */
export interface EligibilityRowDisplayCatalogs {
  eligibilityTierLabels?: Record<string, string>;
}

/**
 * Transform one API patient into the shared config-driven table shape.
 *
 * @param row - Screened eligibility API row
 * @param catalogs - Optional value-to-label maps from the options API
 * @returns Row consumable by `RisaCustomTable`
 */
export function eligibilityRowToTableRow(
  row: EligibilityScreenedRow,
  catalogs: EligibilityRowDisplayCatalogs = {},
): EligibilityTableRow {
  const eligibilityStatusColors = getEligibilityStatusBadgeColors(
    row.eligibility_status,
  );
  const eligibilityTier =
    (row.eligibility_tier &&
      catalogs.eligibilityTierLabels?.[row.eligibility_tier]) ||
    (row.eligibility_tier
      ? formatEligibilityTierLabel(row.eligibility_tier)
      : EMPTY_VALUE);
  return {
    id: row.mrn,
    patientDetails: {
      mainText: row.patient_name || EMPTY_VALUE,
      secondaryText: `MRN : ${row.mrn}`,
      copyValue: row.mrn,
      shouldShowLeftBorder: !row.is_read,
      leftBorderColor: "#0056D6",
      leftBorderWidth: 4,
      primaryTextColor: row.is_read ? undefined : "#0056D6",
    },
    stage: {
      stage: row.stage || "",
      diagnosis: row.diagnosis || "",
    },
    diagnosis: {
      diagnosis: row.diagnosis || "",
      diagnosisCode: row.diagnosis_code || "",
    },
    principalMd: row.principal_md || EMPTY_VALUE,
    nextVisit: formatEligibilityDate(row.next_md_visit),
    additionalTrials: {
      tags: row.additional_studies ?? [],
      linkedTrialIds: (row.monitoring_trials ?? []).map((trial) => trial.trial_id),
    },
    patientStatus: {
      value: row.patient_status,
      sortValue: row.patient_status,
    },
    eligibilityTier: {
      value: row.eligibility_tier,
      text: eligibilityTier,
      backgroundColor: "#E8F1FF",
      foregroundColor: "#0056D6",
    },
    eligibilityStatus: {
      text: row.eligibility_status || EMPTY_VALUE,
      backgroundColor: eligibilityStatusColors.backgroundColor,
      foregroundColor: eligibilityStatusColors.textColor,
    },
    actionItems: {
      mrn: row.mrn,
      isBookmarked: row.is_bookmarked ?? false,
      notificationUnreadCount: row.notifications?.unread_count ?? 0,
      hasUnreadNotification: row.notifications?.has_unread ?? false,
    },
  };
}
